import { createTheme } from '@mui/material/styles';

const brand = { main: '#465FFF', hover: '#3641F5', tint: '#EEF1FF' };

const sidebarBase = {
  text: 'rgba(255,255,255,.78)', icon: 'rgba(255,255,255,.7)', hover: 'rgba(255,255,255,.08)', label: 'rgba(255,255,255,.5)',
  pill: '#FFFFFF', pillText: '#3538CD', pillShadow: '0 4px 14px rgba(16,24,40,.18)',
};

export const CONFETTI = ['#465FFF', '#7C3AED', '#06B6D4', '#F59E0B', '#12B76A', '#F04438'];

export const TAB_FILL = 'radial-gradient(circle at 50% 50%, #7C3AED 0%, #0E7490 100%)';

const KEYFRAMES = {
  '@keyframes cm-tab-ripple': { from: { clipPath: 'circle(0% at 50% 50%)' }, to: { clipPath: 'circle(150% at 50% 50%)' } },
  '@keyframes cm-squish-on': { '0%': { transform: 'scaleX(1)' }, '50%': { transform: 'scaleX(1.3)' }, '100%': { transform: 'scaleX(1)' } },
  '@keyframes cm-squish-off': { '0%': { transform: 'scaleX(1)' }, '50%': { transform: 'scaleX(1.3)' }, '100%': { transform: 'scaleX(1)' } },
  '@keyframes cm-pulse': { '0%': { transform: 'scale(1)' }, '50%': { transform: 'scale(1.06)' }, '100%': { transform: 'scale(1)' } },
  '@keyframes cm-pop': { '0%': { transform: 'scale(0)' }, '60%': { transform: 'scale(1.2)' }, '100%': { transform: 'scale(1)' } },
  '@keyframes cm-tip-pop': { from: { opacity: 0, transform: 'translateX(-50%) scale(.9)' }, to: { opacity: 1, transform: 'translateX(-50%) scale(1)' } },
  '@keyframes cm-draw': { from: { strokeDashoffset: 1 }, to: { strokeDashoffset: 0 } },
  '@keyframes cm-fly': { from: { transform: 'translate(0,0)', opacity: 1 }, to: { transform: 'translate(26px,-22px) rotate(-12deg)', opacity: 0 } },
  '@keyframes cm-return': { from: { transform: 'translate(26px,-22px)', opacity: 0 }, to: { transform: 'translate(0,0)', opacity: 1 } },
  '@keyframes cm-trail': { '0%': { opacity: 0.9, transform: 'rotate(-40deg) scaleX(0)' }, '100%': { opacity: 0, transform: 'rotate(-40deg) scaleX(1)' } },
  '@keyframes cm-flip': { from: { transform: 'rotateX(90deg)', opacity: 0 }, to: { transform: 'rotateX(0)', opacity: 1 } },
  '@keyframes cm-spark': { from: { strokeDashoffset: 0, opacity: 1 }, '85%': { opacity: 1 }, to: { strokeDashoffset: -1, opacity: 0 } },
  '@keyframes cm-launch': { '0%': { transform: 'translateY(0)', opacity: 1 }, '55%': { transform: 'translateY(-30px)', opacity: 0 }, '56%': { transform: 'translateY(14px)', opacity: 0 }, '100%': { transform: 'translateY(0)', opacity: 1 } },
  '@keyframes cm-blast': { from: { transform: 'translateY(0)', opacity: 1 }, to: { transform: 'translateY(-90px)', opacity: 0 } },
  '@keyframes cm-slide-in': { from: { opacity: 0, transform: 'translateY(-4px)' }, to: { opacity: 1, transform: 'translateY(0)' } },
  '@keyframes cm-ring-fill': { from: { strokeDashoffset: 1 }, to: { strokeDashoffset: 0.08 } },
  '@keyframes cm-ring-ripple': { from: { transform: 'scale(1)', opacity: 0.6 }, to: { transform: 'scale(2.2)', opacity: 0 } },
  '@keyframes cm-spin': { to: { transform: 'rotate(360deg)' } },
  '@keyframes cm-spin-rev': { to: { transform: 'rotate(-360deg)' } },
  '@keyframes cm-popper': { '0%,100%': { transform: 'translateX(-50%) rotate(0)' }, '25%': { transform: 'translateX(-50%) rotate(-14deg)' }, '75%': { transform: 'translateX(-50%) rotate(14deg)' } },
  '@keyframes cm-shake': { '0%,100%': { transform: 'translateX(0)' }, '20%,60%': { transform: 'translateX(-6px)' }, '40%,80%': { transform: 'translateX(6px)' } },
};

const tabFill = (selector) => ({
  position: 'relative',
  isolation: 'isolate',
  overflow: 'hidden',
  transition: 'color 200ms',
  '&::before': { content: '""', position: 'absolute', inset: 0, zIndex: -1, background: TAB_FILL, opacity: 0, transition: 'opacity 200ms ease-out' },
  [selector]: { color: '#FFFFFF', backgroundColor: 'transparent', '&::before': { opacity: 1, animation: 'cm-tab-ripple 350ms cubic-bezier(.2,.8,.2,1)' }, '& .MuiListItemIcon-root': { color: '#FFFFFF' } },
  [`${selector}:hover`]: { backgroundColor: 'transparent' },
});

export const CHIP_TONES = {
  error: { bg: '#FEE4E2', fg: '#B42318' },
  warning: { bg: '#FEF0C7', fg: '#93370D' },
  info: { bg: '#D1E9FF', fg: '#175CD3' },
  default: { bg: '#F2F4F7', fg: '#344054' },
  success: { bg: '#DCFAE6', fg: '#067647' },
  primary: { bg: brand.tint, fg: brand.hover },
};

export const GLASS = {
  light: {
    rgb: [255, 255, 255], solid: '#FFFFFF', border: 'rgba(255,255,255,.6)', base: '#F9FAFB', blobs: ['#EEF1FF', '#E0F7FB', '#F5EEFF'],
    sidebar: 'linear-gradient(180deg, rgba(53,56,205,.82) 0%, rgba(45,49,166,.82) 100%)',
  },
  dark: {
    rgb: [23, 28, 43], solid: '#171C2B', border: 'rgba(255,255,255,.08)', base: '#0F1320', blobs: ['#1E2650', '#0B2E36'],
    sidebar: 'linear-gradient(180deg, rgba(22,27,51,.82) 0%, rgba(18,22,43,.82) 100%)',
  },
};
export const GLASS_ALPHA = { card: 0.72, opaque: 0.88, overlay: 0.85, header: 0.65, darkCard: 0.62 };
const NO_BACKDROP = '@supports not ((backdrop-filter: blur(1px)) or (-webkit-backdrop-filter: blur(1px)))';
const LESS_TRANSPARENCY = '@media (prefers-reduced-transparency: reduce)';

export function glass(mode, alpha, blur) {
  const g = GLASS[mode];
  const a = mode === 'dark' && alpha === GLASS_ALPHA.card ? GLASS_ALPHA.darkCard : alpha;
  const filter = `blur(${blur}px) saturate(140%)`;
  const solid = { backgroundColor: g.solid, backdropFilter: 'none', WebkitBackdropFilter: 'none' };
  return {
    backgroundColor: `rgba(${g.rgb.join(',')},${a})`,
    backdropFilter: filter,
    WebkitBackdropFilter: filter,
    [NO_BACKDROP]: solid,
    [LESS_TRANSPARENCY]: solid,
  };
}

export function glassSidebar(mode, solidBg) {
  const filter = 'blur(20px)';
  return {
    background: GLASS[mode].sidebar,
    backdropFilter: filter,
    WebkitBackdropFilter: filter,
    [NO_BACKDROP]: { background: solidBg },
    [LESS_TRANSPARENCY]: { background: solidBg, backdropFilter: 'none', WebkitBackdropFilter: 'none' },
  };
}

const mesh = (mode) => {
  const { base, blobs } = GLASS[mode];
  const spots = ['at 8% 6%', 'at 92% 10%', 'at 70% 92%'];
  return `${blobs.map((c, i) => `radial-gradient(${spots[i]}, ${c} 0, transparent 45%)`).join(', ')}, ${base}`;
};

export const ACCENT = { violet: '#7C3AED', cyan: '#06B6D4', cyanText: '#0E7490' };

const modes = {
  light: {
    background: { default: '#F9FAFB', paper: '#FFFFFF' },
    text: { primary: '#1C2434', secondary: '#64748B', disabled: '#98A2B3' },
    divider: '#E4E7EC',
    error: { main: '#D92D20', dark: '#B42318', light: '#FEE4E2' },
    warning: { main: '#B54708', dark: '#93370D', light: '#FEF0C7' },
    success: { main: '#067647', dark: '#05603A', light: '#DCFAE6' },
    info: { main: '#175CD3', dark: '#1849A9', light: '#D1E9FF' },
    sidebar: { ...sidebarBase, bg: 'linear-gradient(180deg, #3538CD 0%, #2D31A6 100%)' },
  },
  dark: {
    background: { default: '#0F1320', paper: '#171C2B' },
    text: { primary: '#DEE4EE', secondary: '#A6B4C8', disabled: '#64748B' },
    divider: '#2E3A4B',
    error: { main: '#F97066', dark: '#F04438', light: '#55160C' },
    warning: { main: '#FDB022', dark: '#F79009', light: '#4E1D09' },
    success: { main: '#32D583', dark: '#12B76A', light: '#053321' },
    info: { main: '#53B1FD', dark: '#2E90FA', light: '#102A56' },
    sidebar: { ...sidebarBase, bg: 'linear-gradient(180deg, #161B33 0%, #12162B 100%)' },
  },
};

const chipVariants = Object.entries(CHIP_TONES).flatMap(([color, { bg, fg }]) => [
  { props: { color, variant: 'filled' }, style: { backgroundColor: bg, color: fg, '& .MuiChip-icon, & .MuiChip-deleteIcon': { color: fg } } },
  { props: { color, variant: 'outlined' }, style: { borderColor: fg, color: fg } },
]);

function build(mode) {
  const t = modes[mode];
  return createTheme({
    palette: {
      mode,
      primary: { main: brand.main, dark: brand.hover, light: brand.tint, contrastText: '#FFFFFF' },
      secondary: { main: mode === 'light' ? '#475467' : '#A6B4C8' },
      background: t.background,
      text: t.text,
      divider: t.divider,
      error: t.error,
      warning: t.warning,
      success: t.success,
      info: t.info,
      sidebar: t.sidebar,
      accent: { ...ACCENT, check: mode === 'light' ? ACCENT.cyanText : ACCENT.cyan },
    },
    typography: {
      fontFamily: 'Inter, system-ui, -apple-system, "Segoe UI", Roboto, Arial, sans-serif',
      h1: { fontSize: '1.75rem', fontWeight: 700, lineHeight: 1.25, letterSpacing: '-0.01em' },
      h2: { fontSize: '1.5rem', fontWeight: 700, lineHeight: 1.3 },
      h3: { fontSize: '1.375rem', fontWeight: 600, lineHeight: 1.3 },
      h4: { fontSize: '1.375rem', fontWeight: 700, lineHeight: 1.3, letterSpacing: '-0.01em' },
      h5: { fontSize: '1.125rem', fontWeight: 600, lineHeight: 1.4 },
      h6: { fontSize: '1rem', fontWeight: 600, lineHeight: 1.4 },
      body1: { fontSize: '0.875rem', lineHeight: 1.5 },
      body2: { fontSize: '0.8125rem', lineHeight: 1.5 },
      subtitle1: { fontSize: '0.9375rem', fontWeight: 500 },
      subtitle2: { fontSize: '0.8125rem', fontWeight: 500 },
      caption: { fontSize: '0.75rem', lineHeight: 1.5 },
      button: { fontSize: '0.875rem', fontWeight: 600, textTransform: 'none' },
    },
    shape: { borderRadius: 10 },
    components: {
      MuiCssBaseline: {
        styleOverrides: {
          'html, body': { overscrollBehaviorY: 'contain' },
          body: { backgroundColor: t.background.default, background: mesh(mode), backgroundAttachment: 'fixed', color: t.text.primary },
          '*:focus-visible': { outline: `2px solid ${brand.main}`, outlineOffset: 2 },
          ...KEYFRAMES,
          '@media (prefers-reduced-motion: reduce)': {
            '*, *::before, *::after': { animationDuration: '0.01ms !important', animationIterationCount: '1 !important', transitionDuration: '0.01ms !important', scrollBehavior: 'auto !important' },
          },
        },
      },
      MuiPaper: {
        defaultProps: { elevation: 0 },
        styleOverrides: {
          root: {
            backgroundImage: 'none',
            ...glass(mode, GLASS_ALPHA.card, 16),
            '& .MuiPaper-root': { backdropFilter: 'none', WebkitBackdropFilter: 'none' },
          },
          rounded: { borderRadius: 14 },
          outlined: { borderColor: GLASS[mode].border, boxShadow: '0 8px 32px rgba(31,38,135,.10)', ...glass(mode, GLASS_ALPHA.opaque, 16) },
        },
      },
      MuiCard: {
        defaultProps: { elevation: 0 },
        styleOverrides: { root: { borderRadius: 14, border: `1px solid ${GLASS[mode].border}`, boxShadow: '0 8px 32px rgba(31,38,135,.10)', ...glass(mode, GLASS_ALPHA.opaque, 16) } },
      },
      MuiDialog: { styleOverrides: { paper: { borderRadius: 14, ...glass(mode, GLASS_ALPHA.overlay, 24) } } },
      MuiPopover: { styleOverrides: { paper: glass(mode, GLASS_ALPHA.overlay, 24) } },
      MuiAccordion: { styleOverrides: { root: { backgroundColor: 'transparent', backdropFilter: 'none', WebkitBackdropFilter: 'none' } } },
      MuiDrawer: { styleOverrides: { paper: { backgroundColor: 'transparent', backdropFilter: 'none', WebkitBackdropFilter: 'none' } } },
      MuiButton: {
        styleOverrides: {
          root: { borderRadius: 10, minHeight: 40, boxShadow: 'none', '&:hover': { boxShadow: 'none' } },
          containedPrimary: { '&:hover': { backgroundColor: brand.hover } },
        },
      },
      MuiChip: {
        styleOverrides: { root: { borderRadius: 999, fontWeight: 600, fontSize: '0.75rem' } },
        variants: chipVariants,
      },
      MuiOutlinedInput: { styleOverrides: { root: { '&.Mui-error': { animation: 'cm-shake 300ms ease-in-out' } } } },
      MuiFormHelperText: { styleOverrides: { root: { '&.Mui-error': { animation: 'cm-slide-in 200ms ease-out' } } } },
      MuiTabs: { styleOverrides: { indicator: { display: 'none' } } },
      MuiTab: { styleOverrides: { root: { borderRadius: 10, minHeight: 44, ...tabFill('&.Mui-selected') } } },
      MuiToggleButton: { styleOverrides: { root: tabFill('&.Mui-selected') } },
      MuiListItemButton: { styleOverrides: { root: tabFill('&.Mui-selected') } },
      MuiTouchRipple: { styleOverrides: { root: { '& .MuiTouchRipple-rippleVisible': { opacity: 0.32 } } } },
      MuiSwitch: {
        styleOverrides: {
          switchBase: {
            '& .MuiSwitch-thumb': { animation: 'cm-squish-off 200ms ease-out' },
            '&.Mui-checked': { color: '#FFFFFF' },
            '&.Mui-checked .MuiSwitch-thumb': { animation: 'cm-squish-on 200ms ease-out' },
            '&.Mui-checked + .MuiSwitch-track': { backgroundColor: t.text.disabled, opacity: 1, boxShadow: `0 0 6px 1px ${ACCENT.cyan}, 0 0 14px 2px rgba(70,95,255,.45)` },
            '&.Mui-checked + .MuiSwitch-track::before': { transform: 'scaleX(1)' },
            '&.Mui-disabled + .MuiSwitch-track': { opacity: 0.5 },
          },
          track: {
            position: 'relative', overflow: 'hidden', backgroundColor: t.text.disabled, opacity: 1,
            '&::before': { content: '""', position: 'absolute', inset: 0, backgroundColor: brand.main, transform: 'scaleX(0)', transformOrigin: 'right', transition: 'transform 200ms ease-out' },
          },
        },
      },
      MuiAlert: { styleOverrides: { root: { borderRadius: 10, backdropFilter: 'none', WebkitBackdropFilter: 'none' } } },
    },
  });
}

export const lightTheme = build('light');
export const darkTheme = build('dark');
export const themeConfig = { light: lightTheme, dark: darkTheme };
